import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { streamAi } from "@/lib/ai";

export const useAiStatus = () => useQuery({ queryKey: ["ai", "status"], queryFn: async () => (await api.get("/ai/status")).data, staleTime: 5 * 60_000 });

export const AiAssist = ({ path, body, label = "Draft with Claude", onUse, useLabel = "Use This Text", instructionPlaceholder, testId = "ai-assist", compact = false }) => {
  const { data: status } = useAiStatus();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [instruction, setInstruction] = useState("");
  const [open, setOpen] = useState(false);
  const abort = useRef(null);
  useEffect(() => () => { abort.current?.abort(); setOpen(false); setText(""); setBusy(false); }, [path]);
  if (!status?.enabled) return null;
  const run = async () => {
    setBusy(true); setText(""); setOpen(true);
    abort.current = new AbortController();
    try {
      const payload = typeof body === "function" ? body() : body || {};
      await streamAi(path, { ...payload, ...(instruction.trim() ? { instruction: instruction.trim() } : {}) }, setText, abort.current.signal);
    } catch (e) { if (e.name !== "AbortError") { toast.error(e.message); setOpen(false); } } finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Copied."); } catch { toast.error("Could not copy."); } };
  return (
    <div className={compact ? "mt-3" : "mt-4"} data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        {instructionPlaceholder && <input className="input min-h-[40px] flex-1 min-w-[220px] text-sm" value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder={instructionPlaceholder} maxLength={500} aria-label="Instruction for the assistant" data-testid={`${testId}-instruction`} />}
        <button type="button" className="btn-secondary min-h-[40px] text-sm" onClick={run} disabled={busy} data-testid={`${testId}-run`}><Sparkles className="h-4 w-4" aria-hidden="true" /> {busy ? "Drafting…" : label}</button>
      </div>
      {open && (
        <div className="confirm-pop mt-3 border border-olive-900/40 bg-cream-100 p-4" role="region" aria-live="polite" aria-label="Assistant draft" data-testid={`${testId}-panel`}>
          <p className="text-xs text-ink-500">Draft by {status.model} · sent to {status.provider}. Read it carefully and change anything that is not right before you use it.</p>
          <p className="mt-2 whitespace-pre-wrap break-words text-ink-900" data-testid={`${testId}-output`}>{text || (busy ? "Thinking…" : "")}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {onUse && <button type="button" className="btn-primary min-h-[40px] text-sm" disabled={busy || !text} onClick={() => { onUse(text); setOpen(false); }} data-testid={`${testId}-use`}>{useLabel}</button>}
            <button type="button" className="btn-secondary min-h-[40px] text-sm" disabled={busy || !text} onClick={copy} data-testid={`${testId}-copy`}>Copy</button>
            <button type="button" className="btn-quiet min-h-[40px] text-sm" onClick={() => { abort.current?.abort(); setOpen(false); setText(""); }} data-testid={`${testId}-discard`}>{busy ? "Stop" : "Discard"}</button>
          </div>
        </div>
      )}
    </div>
  );
};
